import { useState, useEffect, useCallback } from 'react';
import { isIosSafari, isStandaloneDisplay } from '../utils/pwaHelpers';

/**
 * Estado de instalación de la app en el dispositivo.
 *
 * - Android / Chrome de escritorio: el navegador dispara `beforeinstallprompt`;
 *   se guarda el evento para ofrecer un botón "Instalar" propio.
 * - iPhone / iPad: no hay evento; solo Safari permite "Agregar a pantalla de
 *   inicio", así que se expone `isIos` para mostrar las instrucciones.
 */
export const useInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplay(window));
    const [isIos] = useState(() => isIosSafari(window.navigator));

    useEffect(() => {
        const handleBeforeInstall = (event) => {
            // Evita el mini-aviso automático del navegador: usamos el propio.
            event.preventDefault();
            setDeferredPrompt(event);
        };
        const handleInstalled = () => {
            setDeferredPrompt(null);
            setIsStandalone(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);
        window.addEventListener('appinstalled', handleInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            window.removeEventListener('appinstalled', handleInstalled);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!deferredPrompt) return false;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        // El evento se puede usar una sola vez.
        setDeferredPrompt(null);
        return outcome === 'accepted';
    }, [deferredPrompt]);

    return {
        canInstall: !!deferredPrompt && !isStandalone,
        showIosInstructions: isIos && !isStandalone,
        isStandalone,
        promptInstall,
    };
};
